/**
 * Business Partner Details View
 *
 * Page for viewing business partner details with tabs for overview and CoA mappings.
 */

import { useEffect, useState, useCallback, useMemo, memo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
    ArrowLeft,
    Building2,
    ChevronRight,
    Map as MapIcon,
    Save,
    AlertCircle,
} from "lucide-react";
import { Card, Loader, Button } from "@/ui/atoms";
import {
    EmptyState,
    MultiSelectColumnFilter,
    SearchableSelect,
    SearchableCheckboxList,
    type TSearchableOption,
} from "@/ui/molecules";
import { OrganismCoAVersions } from "@/ui/organisms/OrganismCoAVersions";
import { routerPaths } from "@/ui/router";
import { cn } from "@/utils/utils";
import type { TBusinessPartnerType } from "@/store";
import {
    fetchCoAVersions,
    uploadCoAVersion,
    createCoASnapshot,
    restoreCoAVersion,
} from "@/useCases/coaVersions";
import { downloadXlsx } from "@/utils/downloadXlsx";
import {
    collectStringMultiSelectOptions,
    stringCellFilterKey,
} from "@/utils/tableMultiSelectFilter";

/**
 * The BP-details CoA-mapping dropdowns filter the full master account list
 * (all active, non-prohibited accounts) client-side. `SearchableSelect` is
 * async-only, so this thin adapter feeds the static list through `onSearch` and
 * bridges the id-based value/onChange to the component's `SearchableOption`
 * value/onSelect. `searchOnOpen` makes the entire master list browsable on open
 * (not just after typing), and every match is returned so all master data shows.
 */
type TSearchableSelectOption = { id: string; label: string; searchText: string };

function LocalSearchableSelect({
    options,
    value,
    onChange,
    placeholder,
    disabled,
    className,
}: {
    options: TSearchableSelectOption[];
    value: string | null | undefined;
    onChange: (id: string) => void;
    placeholder?: string;
    disabled?: boolean;
    className?: string;
}) {
    const selected = value ? (options.find((o) => o.id === value) ?? null) : null;
    const seed: TSearchableOption | null = selected
        ? { value: selected.id, label: selected.label }
        : null;
    const control = (
        <SearchableSelect
            searchOnOpen
            value={seed}
            placeholder={placeholder}
            disabled={disabled}
            onSearch={(query) => {
                const q = query.trim().toLowerCase();
                const matches = q ? options.filter((o) => o.searchText.includes(q)) : options;
                return Promise.resolve(matches.map((o) => ({ value: o.id, label: o.label })));
            }}
            onSelect={(option) => onChange(option ? option.value : "")}
        />
    );
    return className ? <div className={className}>{control}</div> : control;
}

/**
 * CoA Level type
 */
export type TCoALevel = "A" | "P" | "C";

/**
 * CoA Mapping view type
 */
export type TCoAMappingView = {
    id: string;
    thirdAccountCode: string;
    /** Second key component; null on single-key rows (including the fallback). */
    thirdAccountCode2: string | null;
    /** True for the row used when a line supplies no second component. */
    isFallback: boolean;
    thirdAccountName: string;
    yardiAccountId: string;
    yardiAccountCode: string | null;
    yardiAccountName: string | null;
    level: TCoALevel;
    isInForce: boolean;
    isActive: boolean;
    createdAt: string;
    updatedAt: string;
};

/**
 * Business Partner view type
 */
export type TServiceProviderView = {
    id: string;
    companyId: string;
    name: string;
    code: string;
    businessPartnerType: TBusinessPartnerType;
    isActive: boolean;
    contactEmail: string | null;
    canUploadSupportingFiles: boolean;
    coaMappingMode: "single" | "multi_criteria";
    isTest: boolean;
    employees: Array<{ id: string; display_name: string; email: string }>;
    createdAt: string;
    updatedAt: string;
};

type TTabId = "overview" | "coa";

const tabs: { id: TTabId; label: string; icon: typeof Building2 }[] = [
    { id: "overview", label: "Overview", icon: Building2 },
    { id: "coa", label: "CoA Mappings", icon: MapIcon },
];

const businessPartnerTypeLabels: Record<TBusinessPartnerType, string> = {
    service_provider: "Service Provider",
    portfolio_company: "Portfolio Company",
};

export type TViewBusinessPartnerDetailsParams = {
    useCases: () => {
        onMount: (businessPartnerId: string) => void;
        onUnmount: () => void;
        useBusinessPartnerState: () => {
            data: TServiceProviderView | null;
            loading: boolean;
            error: string | null;
        };
        useCoAMappings: () => {
            data: TCoAMappingView[] | null;
            loading: boolean;
        };
        onCreateCoAMapping: (
            businessPartnerId: string,
            businessPartnerType: TBusinessPartnerType,
            data: {
                third_account_code: string;
                third_account_name: string;
                yardi_account_id: string;
                level: TCoALevel;
            },
        ) => Promise<void>;
        onUpdateCoAMapping: (
            businessPartnerId: string,
            businessPartnerType: TBusinessPartnerType,
            mappingId: string,
            data: {
                level?: TCoALevel;
                is_active?: boolean;
                yardi_account_id?: string;
                is_in_force?: boolean;
            },
        ) => Promise<void>;
        onDeleteCoAMapping: (
            businessPartnerId: string,
            businessPartnerType: TBusinessPartnerType,
            mappingId: string,
        ) => Promise<void>;
        useChartOfAccountsList: () => Array<{
            id: string;
            number: string;
            shortName: string;
            isProhibited: boolean;
            isActive: boolean;
        }>;
        onUpdateBusinessPartner: (
            businessPartnerId: string,
            businessPartnerType: TBusinessPartnerType,
            data: {
                name?: string;
                canUploadSupportingFiles?: boolean;
                coaMappingMode?: "single" | "multi_criteria";
                isActive?: boolean;
                isTest?: boolean;
                employeeIds?: string[];
            },
        ) => Promise<void>;
        useUsers: () => {
            data: Array<{ id: string; displayName: string; email: string }> | null;
            loading: boolean;
        };
        useExistingNames: () => string[];
        canEdit: boolean;
        canDeactivate: boolean;
        canEditEmployees: boolean;
        canUploadCoa: boolean;
        canEditCoaLines: boolean;
        /** Environment flag, not a permission — the feature is absent when off. */
        multiCriteriaCoaAvailable: boolean;
    };
    store: any; // TAppStore from @/store
};

export type TViewBusinessPartnerDetailsProps = Record<string, never>;

export const createViewBusinessPartnerDetails = ({
    useCases,
    store,
}: TViewBusinessPartnerDetailsParams) => {
    return function ViewBusinessPartnerDetails() {
        const { businessPartnerId } = useParams<{ businessPartnerId: string }>();
        const navigate = useNavigate();
        const {
            onMount,
            onUnmount,
            useBusinessPartnerState,
            useCoAMappings,
            onCreateCoAMapping,
            onUpdateCoAMapping,
            onDeleteCoAMapping,
            useChartOfAccountsList,
            onUpdateBusinessPartner,
            useUsers,
            useExistingNames,
            canEdit,
            canDeactivate,
            canEditEmployees,
            canUploadCoa,
            canEditCoaLines,
            multiCriteriaCoaAvailable,
        } = useCases();

        const businessPartnerState = useBusinessPartnerState();
        const coaMappingsState = useCoAMappings();
        const existingNames = useExistingNames();
        const chartOfAccountsList = useChartOfAccountsList();
        const availableAccounts = useMemo(
            () => chartOfAccountsList.filter((a) => a.isActive && !a.isProhibited),
            [chartOfAccountsList],
        );
        // Options for the searchable account dropdowns. Built once here (not per
        // row) so the arrays stay stable references and React.memo'd rows aren't
        // re-rendered. Both columns search by number OR name via `searchText`.
        const numberOptions = useMemo<TSearchableSelectOption[]>(
            () =>
                availableAccounts.map((a) => ({
                    id: a.id,
                    label: a.number,
                    searchText: `${a.number} ${a.shortName}`.toLowerCase(),
                })),
            [availableAccounts],
        );
        const nameOptions = useMemo<TSearchableSelectOption[]>(
            () =>
                availableAccounts.map((a) => ({
                    id: a.id,
                    label: a.shortName,
                    searchText: `${a.number} ${a.shortName}`.toLowerCase(),
                })),
            [availableAccounts],
        );

        // Column filters (search + checkboxes) for the CoA mappings table.
        // Empty array = no filter on that column (show all).
        const [sourceCodeFilter, setSourceCodeFilter] = useState<string[]>([]);
        const [sourceCode2Filter, setSourceCode2Filter] = useState<string[]>([]);
        const [sourceNameFilter, setSourceNameFilter] = useState<string[]>([]);
        const [acctCodeFilter, setAcctCodeFilter] = useState<string[]>([]);
        const [acctNameFilter, setAcctNameFilter] = useState<string[]>([]);

        // Filter options are derived from the full (unfiltered) data so the choices
        // stay stable as the user narrows the table.
        const sourceCodeOptions = useMemo(
            () =>
                collectStringMultiSelectOptions(
                    coaMappingsState.data ?? [],
                    (m) => m.thirdAccountCode,
                ),
            [coaMappingsState.data],
        );
        const sourceCode2Options = useMemo(
            () =>
                collectStringMultiSelectOptions(
                    coaMappingsState.data ?? [],
                    (m) => m.thirdAccountCode2,
                ),
            [coaMappingsState.data],
        );
        const sourceNameOptions = useMemo(
            () =>
                collectStringMultiSelectOptions(
                    coaMappingsState.data ?? [],
                    (m) => m.thirdAccountName,
                ),
            [coaMappingsState.data],
        );
        const acctCodeOptions = useMemo(
            () =>
                collectStringMultiSelectOptions(
                    coaMappingsState.data ?? [],
                    (m) => m.yardiAccountCode,
                ),
            [coaMappingsState.data],
        );
        const acctNameOptions = useMemo(
            () =>
                collectStringMultiSelectOptions(
                    coaMappingsState.data ?? [],
                    (m) => m.yardiAccountName,
                ),
            [coaMappingsState.data],
        );

        const filteredMappings = useMemo(() => {
            const data = coaMappingsState.data ?? [];
            const matches = (selected: string[], raw: string | null | undefined) =>
                selected.length === 0 || selected.includes(stringCellFilterKey(raw));
            return data.filter(
                (m) =>
                    matches(sourceCodeFilter, m.thirdAccountCode) &&
                    matches(sourceCode2Filter, m.thirdAccountCode2) &&
                    matches(sourceNameFilter, m.thirdAccountName) &&
                    matches(acctCodeFilter, m.yardiAccountCode) &&
                    matches(acctNameFilter, m.yardiAccountName),
            );
        }, [
            coaMappingsState.data,
            sourceCodeFilter,
            sourceCode2Filter,
            sourceNameFilter,
            acctCodeFilter,
            acctNameFilter,
        ]);

        const groupedMappings = useMemo(() => {
            const groups = new Map<
                string,
                { thirdAccountCode: string; thirdAccountName: string; mappings: TCoAMappingView[] }
            >();
            for (const m of filteredMappings) {
                const existing = groups.get(m.thirdAccountCode);
                if (existing) {
                    existing.mappings.push(m);
                } else {
                    groups.set(m.thirdAccountCode, {
                        thirdAccountCode: m.thirdAccountCode,
                        thirdAccountName: m.thirdAccountName,
                        mappings: [m],
                    });
                }
            }
            return Array.from(groups.values());
        }, [filteredMappings]);

        const [activeTab, setActiveTab] = useState<TTabId>("overview");
        const [showCreateRow, setShowCreateRow] = useState(false);

        // Users for employee selection
        const usersState = useUsers();

        const bpType = businessPartnerState.data?.businessPartnerType;

        // The second key component is shown only where it can exist: the feature
        // is present in this deployment, and this partner is multi-criteria.
        // Read from the *saved* partner, not the overview form's draft toggle, so
        // the table does not reshape while the user is still editing the checkbox.
        const showSecondComponent =
            multiCriteriaCoaAvailable &&
            businessPartnerState.data?.coaMappingMode === "multi_criteria";

        // Editable overview form state
        const [name, setName] = useState("");
        const [canUploadSupportingFiles, setCanUploadSupportingFiles] = useState(false);
        const [multiCriteriaCoa, setMultiCriteriaCoa] = useState(false);
        const [isTest, setIsTest] = useState(false);
        const [isActive, setIsActive] = useState(false);
        const [employeeIds, setEmployeeIds] = useState<string[]>([]);
        const [saving, setSaving] = useState(false);
        const [saveError, setSaveError] = useState<string | null>(null);
        const [saveSuccess, setSaveSuccess] = useState(false);

        // Sync form state when business partner data loads/changes
        useEffect(() => {
            if (businessPartnerState.data) {
                setName(businessPartnerState.data.name);
                setCanUploadSupportingFiles(businessPartnerState.data.canUploadSupportingFiles);
                setMultiCriteriaCoa(businessPartnerState.data.coaMappingMode === "multi_criteria");
                setIsTest(businessPartnerState.data.isTest);
                setIsActive(businessPartnerState.data.isActive);
                setEmployeeIds(businessPartnerState.data.employees.map((e) => e.id));
            }
        }, [businessPartnerState.data]);

        const handleSaveOverview = useCallback(async () => {
            if (!businessPartnerId || !bpType) return;
            if (canEditEmployees && employeeIds.length === 0) return;
            const trimmedName = name.trim();
            if (canEdit && !trimmedName) {
                setSaveError("Name is required");
                return;
            }
            if (canEdit) {
                const isDuplicate = existingNames.some(
                    (n) =>
                        n.toLowerCase() === trimmedName.toLowerCase() &&
                        n.toLowerCase() !== businessPartnerState.data?.name.toLowerCase(),
                );
                if (isDuplicate) {
                    setSaveError(`A business partner named "${trimmedName}" already exists`);
                    return;
                }
            }
            setSaving(true);
            setSaveError(null);
            setSaveSuccess(false);
            try {
                await onUpdateBusinessPartner(businessPartnerId, bpType, {
                    name: trimmedName,
                    canUploadSupportingFiles,
                    // Only sent while the environment flag is on. Sending it
                    // otherwise would 400 every save on an unrelated field.
                    ...(multiCriteriaCoaAvailable
                        ? {
                              coaMappingMode: (multiCriteriaCoa ? "multi_criteria" : "single") as
                                  | "single"
                                  | "multi_criteria",
                          }
                        : {}),
                    isActive,
                    isTest,
                    employeeIds,
                });
                setSaveSuccess(true);
                setTimeout(() => setSaveSuccess(false), 3000);
            } catch (err) {
                setSaveError(err instanceof Error ? err.message : "Failed to save changes");
            } finally {
                setSaving(false);
            }
        }, [
            businessPartnerId,
            bpType,
            name,
            existingNames,
            businessPartnerState.data,
            canUploadSupportingFiles,
            multiCriteriaCoa,
            multiCriteriaCoaAvailable,
            isActive,
            isTest,
            employeeIds,
            onUpdateBusinessPartner,
            canEdit,
            canEditEmployees,
        ]);

        useEffect(() => {
            if (businessPartnerId) {
                onMount(businessPartnerId);
            }
            return onUnmount;
        }, [businessPartnerId, onMount, onUnmount]);

        const handleBack = useCallback(() => {
            navigate(routerPaths.admin.businessPartners);
        }, [navigate]);

        const handleCreateSave = useCallback(
            async (data: {
                third_account_code: string;
                third_account_name: string;
                yardi_account_id: string;
                level: TCoALevel;
            }) => {
                if (!businessPartnerId || !bpType) return;
                await onCreateCoAMapping(businessPartnerId, bpType, data);
                setShowCreateRow(false);
            },
            [businessPartnerId, bpType, onCreateCoAMapping],
        );

        const handleCreateCancel = useCallback(() => setShowCreateRow(false), []);

        const handleMappingUpdate = useCallback(
            async (
                mappingId: string,
                data: {
                    level?: TCoALevel;
                    is_active?: boolean;
                    yardi_account_id?: string;
                    is_in_force?: boolean;
                },
            ) => {
                if (!businessPartnerId || !bpType) return;
                await onUpdateCoAMapping(businessPartnerId, bpType, mappingId, data);
            },
            [businessPartnerId, bpType, onUpdateCoAMapping],
        );

        const handleMappingDelete = useCallback(
            async (mappingId: string) => {
                if (!businessPartnerId || !bpType) return;
                await onDeleteCoAMapping(businessPartnerId, bpType, mappingId);
            },
            [businessPartnerId, bpType, onDeleteCoAMapping],
        );

        const memoizedCoaVersionActions = useMemo(
            () => ({
                fetchVersions: (bpId: string, bpType: TBusinessPartnerType) =>
                    fetchCoAVersions(store, bpId, bpType),
                uploadVersion: (
                    bpId: string,
                    bpType: TBusinessPartnerType,
                    file: File,
                    desc?: string,
                ) => uploadCoAVersion(store, bpId, bpType, file, desc),
                createSnapshot: (bpId: string, bpType: TBusinessPartnerType, desc?: string) =>
                    createCoASnapshot(store, bpId, bpType, desc),
                restoreVersion: (bpId: string, bpType: TBusinessPartnerType, vId: string) =>
                    restoreCoAVersion(store, bpId, bpType, vId),
            }),
            [store, fetchCoAVersions, uploadCoAVersion, createCoASnapshot, restoreCoAVersion],
        );

        const handleVersionChange = useCallback(() => {
            if (businessPartnerId) {
                onMount(businessPartnerId);
            }
        }, [businessPartnerId, onMount]);

        if (businessPartnerState.loading && !businessPartnerState.data) {
            return (
                <div className="flex items-center justify-center py-12">
                    <Loader size="lg" />
                </div>
            );
        }

        if (businessPartnerState.error || !businessPartnerState.data) {
            return (
                <div className="space-y-6">
                    <div className="flex items-center justify-between">
                        <h1 className="text-2xl font-semibold">Business Partner Details</h1>
                        <Button variant="outline" onClick={handleBack}>
                            <ArrowLeft className="h-4 w-4 mr-2" />
                            Back to Business Partners
                        </Button>
                    </div>
                    <Card className="p-12">
                        <EmptyState
                            title="Business partner not found"
                            description={
                                businessPartnerState.error ||
                                "The business partner you're looking for doesn't exist."
                            }
                        />
                    </Card>
                </div>
            );
        }

        const businessPartner = businessPartnerState.data;

        return (
            <div className="space-y-6">
                {/* Header with title and actions */}
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl font-semibold">{businessPartner.name}</h1>
                        <p className="text-sm text-muted-foreground mt-1">
                            <button
                                onClick={handleBack}
                                className="hover:text-foreground hover:underline"
                            >
                                Business Partners
                            </button>
                            {" > "}
                            <span>{businessPartner.name}</span>
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        <Button variant="outline" onClick={handleBack}>
                            <ArrowLeft className="h-4 w-4 mr-2" />
                            Back to Business Partners
                        </Button>
                    </div>
                </div>

                {/* Tabs */}
                <div className="border-b">
                    <nav className="flex gap-6" aria-label="Tabs">
                        {tabs.map((tab) => (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={cn(
                                    "flex items-center gap-2 py-3 text-sm font-medium border-b-2 -mb-px transition-colors",
                                    activeTab === tab.id
                                        ? "border-primary text-primary"
                                        : "border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground",
                                )}
                            >
                                <tab.icon className="h-4 w-4" />
                                {tab.label}
                            </button>
                        ))}
                    </nav>
                </div>

                {/* Tab Content */}
                <div>
                    {/* Overview Tab */}
                    {activeTab === "overview" && (
                        <Card className="p-6">
                            <div className="space-y-6">
                                {/* Fields */}
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                    <div className="p-4 bg-muted/50 rounded-lg">
                                        {canEdit ? (
                                            <>
                                                <label className="text-xs text-muted-foreground mb-1 block">
                                                    Name <span className="text-destructive">*</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    value={name}
                                                    onChange={(e) => setName(e.target.value)}
                                                    className="w-full px-2 py-1 text-sm border rounded-md bg-background font-medium"
                                                />
                                            </>
                                        ) : (
                                            <>
                                                <p className="text-xs text-muted-foreground mb-1">
                                                    Name
                                                </p>
                                                <p className="text-sm font-medium">
                                                    {businessPartner.name}
                                                </p>
                                            </>
                                        )}
                                    </div>
                                    <div className="p-4 bg-muted/50 rounded-lg">
                                        <p className="text-xs text-muted-foreground mb-1">Type</p>
                                        <p className="text-sm">
                                            {
                                                businessPartnerTypeLabels[
                                                    businessPartner.businessPartnerType
                                                ]
                                            }
                                        </p>
                                    </div>
                                </div>

                                {/* Options - editable for permitted roles, read-only otherwise */}
                                <div className="space-y-3 pt-4 border-t">
                                    <p className="text-sm font-medium text-muted-foreground">
                                        Options
                                    </p>
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                        <label
                                            className={cn(
                                                "flex items-center gap-2 p-3 rounded-lg border",
                                                canDeactivate
                                                    ? "cursor-pointer hover:bg-muted/50 transition-colors"
                                                    : "opacity-70",
                                            )}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={isActive}
                                                disabled={!canDeactivate}
                                                onChange={
                                                    canDeactivate
                                                        ? (e) => {
                                                              const active = e.target.checked;
                                                              setIsActive(active);
                                                              if (active)
                                                                  setCanUploadSupportingFiles(true);
                                                          }
                                                        : undefined
                                                }
                                                className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                                            />
                                            <span className="text-sm">Active</span>
                                        </label>
                                        <label
                                            className={cn(
                                                "flex items-center gap-2 p-3 rounded-lg border",
                                                canEdit
                                                    ? "cursor-pointer hover:bg-muted/50 transition-colors"
                                                    : "opacity-70",
                                            )}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={isTest}
                                                disabled={!canEdit}
                                                onChange={
                                                    canEdit
                                                        ? (e) => setIsTest(e.target.checked)
                                                        : undefined
                                                }
                                                className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                                            />
                                            <span className="text-sm">Test user</span>
                                        </label>
                                        <label
                                            className={cn(
                                                "flex items-center gap-2 p-3 rounded-lg border",
                                                canEdit && !isActive
                                                    ? "cursor-pointer hover:bg-muted/50 transition-colors"
                                                    : "opacity-70",
                                            )}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={canUploadSupportingFiles}
                                                disabled={!(canEdit && !isActive)}
                                                onChange={
                                                    canEdit && !isActive
                                                        ? (e) =>
                                                              setCanUploadSupportingFiles(
                                                                  e.target.checked,
                                                              )
                                                        : undefined
                                                }
                                                className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                                            />
                                            <span className="text-sm">Upload Supporting Files</span>
                                        </label>
                                        {/* Hidden entirely where the environment does not
                                            have multi-criteria CoA mapping. The API
                                            rejects the value too — this is presentation
                                            only, not the gate. */}
                                        {multiCriteriaCoaAvailable ? (
                                            <label
                                                className={cn(
                                                    "flex items-center gap-2 p-3 rounded-lg border",
                                                    canEdit
                                                        ? "cursor-pointer hover:bg-muted/50 transition-colors"
                                                        : "opacity-70",
                                                )}
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={multiCriteriaCoa}
                                                    disabled={!canEdit}
                                                    onChange={
                                                        canEdit
                                                            ? (e) =>
                                                                  setMultiCriteriaCoa(
                                                                      e.target.checked,
                                                                  )
                                                            : undefined
                                                    }
                                                    className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                                                />
                                                <span className="text-sm">
                                                    Multi-criteria CoA mapping
                                                </span>
                                            </label>
                                        ) : null}
                                    </div>
                                </div>

                                {/* Employees - editable for permitted roles, read-only otherwise */}
                                <div className="space-y-3 pt-4 border-t">
                                    <p className="text-sm font-medium text-muted-foreground">
                                        Employees
                                        {canEditEmployees && (
                                            <span className="text-destructive"> *</span>
                                        )}
                                    </p>
                                    {canEditEmployees ? (
                                        <>
                                            <SearchableCheckboxList
                                                options={(usersState.data ?? []).map((user) => ({
                                                    id: user.id,
                                                    label: user.displayName,
                                                    sublabel: user.email,
                                                }))}
                                                selectedIds={employeeIds}
                                                onChange={setEmployeeIds}
                                                loading={usersState.loading}
                                                searchPlaceholder="Search employees..."
                                                emptyMessage="No users available"
                                                loadingMessage="Loading users..."
                                            />
                                            {employeeIds.length > 0 && (
                                                <p className="text-xs text-muted-foreground">
                                                    {employeeIds.length} employee
                                                    {employeeIds.length !== 1 ? "s" : ""} selected
                                                </p>
                                            )}
                                            {employeeIds.length === 0 && (
                                                <p className="text-xs text-destructive">
                                                    At least one employee is required
                                                </p>
                                            )}
                                        </>
                                    ) : (
                                        <div className="border rounded-lg p-3 max-h-48 overflow-y-auto space-y-1 bg-background">
                                            {businessPartner.employees.length > 0 ? (
                                                businessPartner.employees.map((emp) => (
                                                    <div key={emp.id} className="text-sm p-2">
                                                        {emp.display_name}
                                                        <span className="text-muted-foreground ml-1">
                                                            ({emp.email})
                                                        </span>
                                                    </div>
                                                ))
                                            ) : (
                                                <p className="text-sm text-muted-foreground text-center py-2">
                                                    No employees
                                                </p>
                                            )}
                                        </div>
                                    )}
                                </div>

                                {/* Save button and status */}
                                {(canEdit || canDeactivate || canEditEmployees) && (
                                    <div className="flex items-center gap-4 pt-4 border-t">
                                        <Button
                                            onClick={handleSaveOverview}
                                            disabled={
                                                saving ||
                                                (canEditEmployees && employeeIds.length === 0) ||
                                                (canEdit && !name.trim())
                                            }
                                        >
                                            {saving ? (
                                                <>
                                                    <Loader size="sm" className="mr-2" />
                                                    Saving...
                                                </>
                                            ) : (
                                                <>
                                                    <Save className="h-4 w-4 mr-2" />
                                                    Save Changes
                                                </>
                                            )}
                                        </Button>
                                        {saveError && (
                                            <p className="text-sm text-destructive flex items-center gap-1">
                                                <AlertCircle className="h-4 w-4" />
                                                {saveError}
                                            </p>
                                        )}
                                        {saveSuccess && (
                                            <p className="text-sm text-green-600">
                                                Changes saved successfully.
                                            </p>
                                        )}
                                    </div>
                                )}
                            </div>
                        </Card>
                    )}

                    {/* CoA Mappings Tab */}
                    {activeTab === "coa" && (
                        <div className="space-y-6">
                            {/* Version Management Section */}
                            {canUploadCoa && (
                                <Card className="p-6">
                                    <OrganismCoAVersions
                                        businessPartnerId={businessPartner.id}
                                        businessPartnerType={businessPartner.businessPartnerType}
                                        store={store}
                                        coaVersionActions={memoizedCoaVersionActions}
                                        onVersionChange={handleVersionChange}
                                    />
                                </Card>
                            )}

                            {/* Current CoA Mappings Section */}
                            <Card className="p-6">
                                <div className="flex justify-end gap-2 mb-4">
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        disabled={
                                            !coaMappingsState.data ||
                                            coaMappingsState.data.length === 0
                                        }
                                        onClick={() => {
                                            if (!coaMappingsState.data) return;
                                            downloadXlsx(
                                                "coa-mappings",
                                                ...coaMappingsSheet(
                                                    coaMappingsState.data,
                                                    showSecondComponent,
                                                ),
                                            );
                                        }}
                                    >
                                        Download
                                    </Button>
                                    {canEditCoaLines && (
                                        <Button
                                            size="sm"
                                            variant="default"
                                            onClick={() => setShowCreateRow(true)}
                                            disabled={showCreateRow}
                                        >
                                            Add new
                                        </Button>
                                    )}
                                </div>
                                {coaMappingsState.loading ? (
                                    <div className="flex items-center justify-center py-12">
                                        <Loader size="lg" />
                                    </div>
                                ) : coaMappingsState.data &&
                                  (coaMappingsState.data.length > 0 || showCreateRow) ? (
                                    <div className="border rounded-lg overflow-hidden">
                                        <div className="max-h-[600px] overflow-y-auto">
                                            <table className="w-full text-sm">
                                                <thead className="bg-muted sticky top-0">
                                                    <tr>
                                                        <th className="px-4 py-3 text-left font-medium group">
                                                            <span className="flex items-center gap-1">
                                                                Source Acct #
                                                                <MultiSelectColumnFilter
                                                                    label="Source Acct #"
                                                                    options={sourceCodeOptions}
                                                                    selected={sourceCodeFilter}
                                                                    onChange={setSourceCodeFilter}
                                                                />
                                                            </span>
                                                        </th>
                                                        {showSecondComponent && (
                                                            <th className="px-4 py-3 text-left font-medium group">
                                                                <span className="flex items-center gap-1">
                                                                    Source Acct # 2
                                                                    <MultiSelectColumnFilter
                                                                        label="Source Acct # 2"
                                                                        options={sourceCode2Options}
                                                                        selected={sourceCode2Filter}
                                                                        onChange={
                                                                            setSourceCode2Filter
                                                                        }
                                                                    />
                                                                </span>
                                                            </th>
                                                        )}
                                                        <th className="px-4 py-3 text-left font-medium group">
                                                            <span className="flex items-center gap-1">
                                                                Source Acct Name
                                                                <MultiSelectColumnFilter
                                                                    label="Source Acct Name"
                                                                    options={sourceNameOptions}
                                                                    selected={sourceNameFilter}
                                                                    onChange={setSourceNameFilter}
                                                                />
                                                            </span>
                                                        </th>
                                                        <th className="px-4 py-3 text-left font-medium">
                                                            Propcode Level
                                                        </th>
                                                        <th className="px-4 py-3 text-left font-medium group">
                                                            <span className="flex items-center gap-1">
                                                                Acct #
                                                                <MultiSelectColumnFilter
                                                                    label="Acct #"
                                                                    options={acctCodeOptions}
                                                                    selected={acctCodeFilter}
                                                                    onChange={setAcctCodeFilter}
                                                                />
                                                            </span>
                                                        </th>
                                                        <th className="px-4 py-3 text-left font-medium group">
                                                            <span className="flex items-center gap-1">
                                                                Account Name
                                                                <MultiSelectColumnFilter
                                                                    label="Account Name"
                                                                    options={acctNameOptions}
                                                                    selected={acctNameFilter}
                                                                    onChange={setAcctNameFilter}
                                                                />
                                                            </span>
                                                        </th>
                                                        {showSecondComponent && (
                                                            <>
                                                                <th
                                                                    className="px-4 py-3 text-left font-medium"
                                                                    title="Exclude from 2nd mapping — Yes marks the account's fallback row"
                                                                >
                                                                    Exclude
                                                                </th>
                                                                <th className="px-4 py-3 text-left font-medium">
                                                                    Type
                                                                </th>
                                                            </>
                                                        )}
                                                        <th className="px-4 py-3 text-right font-medium w-[140px]">
                                                            Actions
                                                        </th>
                                                    </tr>
                                                </thead>
                                                <tbody className="divide-y">
                                                    {showCreateRow && (
                                                        <CreateMappingRow
                                                            numberOptions={numberOptions}
                                                            nameOptions={nameOptions}
                                                            onSave={handleCreateSave}
                                                            onCancel={handleCreateCancel}
                                                            showSecondComponent={
                                                                showSecondComponent
                                                            }
                                                        />
                                                    )}
                                                    {groupedMappings.map((group) => (
                                                        <MappingGroupRow
                                                            key={group.thirdAccountCode}
                                                            group={group}
                                                            numberOptions={numberOptions}
                                                            nameOptions={nameOptions}
                                                            onUpdate={handleMappingUpdate}
                                                            onDelete={handleMappingDelete}
                                                            canEditCoaLines={canEditCoaLines}
                                                            showSecondComponent={
                                                                showSecondComponent
                                                            }
                                                        />
                                                    ))}
                                                    {groupedMappings.length === 0 &&
                                                        !showCreateRow && (
                                                            <tr>
                                                                <td
                                                                    // 6 base columns, plus
                                                                    // Source Acct # 2, Exclude
                                                                    // and Type.
                                                                    colSpan={
                                                                        showSecondComponent ? 9 : 6
                                                                    }
                                                                    className="px-4 py-8 text-center text-muted-foreground"
                                                                >
                                                                    No mappings match the selected
                                                                    filters.
                                                                </td>
                                                            </tr>
                                                        )}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                ) : (
                                    <p className="text-sm text-muted-foreground text-center py-8 bg-muted/30 rounded-lg">
                                        No CoA mappings configured for this business partner.
                                    </p>
                                )}
                            </Card>
                        </div>
                    )}
                </div>
            </div>
        );
    };
};

const levelLabels: Record<TCoALevel, string> = {
    A: "All",
    P: "Property",
    C: "Company",
};

type TMappingGroup = {
    thirdAccountCode: string;
    thirdAccountName: string;
    mappings: TCoAMappingView[];
};

type TUpdateFn = (
    mappingId: string,
    data: {
        level?: TCoALevel;
        is_active?: boolean;
        yardi_account_id?: string;
        is_in_force?: boolean;
    },
) => Promise<void>;

type TDeleteFn = (mappingId: string) => Promise<void>;

// An existing mapping may point to an account that is no longer active/allowed
// and thus absent from the option list. Prepend it (from the mapping's own
// stored code/name) so the current value still displays and is preserved on save.
const ensureCurrentOption = (
    options: TSearchableSelectOption[],
    mapping: TCoAMappingView,
    label: string | null,
): TSearchableSelectOption[] => {
    if (!mapping.yardiAccountId || options.some((o) => o.id === mapping.yardiAccountId)) {
        return options;
    }
    return [
        {
            id: mapping.yardiAccountId,
            label: label ?? mapping.yardiAccountCode ?? mapping.yardiAccountId,
            searchText:
                `${mapping.yardiAccountCode ?? ""} ${mapping.yardiAccountName ?? ""}`.toLowerCase(),
        },
        ...options,
    ];
};

// Values of the uploader's "Type" column (ROW_TYPE_* in coa_upload/parser.py).
const ROW_TYPE_TWO_COLUMNS = "2-columns";
const ROW_TYPE_STANDARD = "Standard";

/**
 * Type and Exclude as the CoA file spells them.
 *
 * Neither is stored: both are read back off the row. A row carrying a secondary
 * component is a "2-columns" row; everything else is "Standard". Exclude is
 * "Yes" on exactly the account's fallback row. The table and the download derive
 * them the same way, so what a user reads on screen is what a re-upload of the
 * downloaded sheet would say.
 */
export function mappingRowType(mapping: TCoAMappingView): string {
    return mapping.thirdAccountCode2 ? ROW_TYPE_TWO_COLUMNS : ROW_TYPE_STANDARD;
}

export function mappingExcludeLabel(mapping: TCoAMappingView): string {
    return mapping.isFallback ? "Yes" : "No";
}

/**
 * Headers and rows for the CoA mappings download.
 *
 * The sheet is meant to be edited and re-uploaded (.xlsx because the CoA upload
 * accepts nothing else), so it has to be a file the uploader accepts. The parser
 * treats Type / Exclude from 2nd mapping / Third Account Code - Secondary as
 * all-or-nothing: a sheet carrying only some of them is rejected outright. So the
 * three either all appear — for a multi-criteria partner — or none do, which
 * leaves single-key partners with exactly the columns they had before.
 *
 * Type and Exclude are derived, not stored: a row with a secondary component is a
 * "2-columns" row, the fallback is the "Standard" row excluded from the second
 * mapping, and every other row is a plain "Standard". Emitting them blank would
 * fail row validation on re-upload.
 */
export function coaMappingsSheet(
    mappings: readonly TCoAMappingView[],
    includeSecondComponent: boolean,
): [readonly string[], readonly Record<string, string>[]] {
    const headers = includeSecondComponent
        ? ([
              "Third Account Code",
              "Third Account Code - Secondary",
              "Third Account Name",
              "Platform Account Name",
              "Platform Account Code",
              "Level",
              "Type",
              "Exclude from 2nd mapping",
          ] as const)
        : ([
              "Third Account Code",
              "Third Account Name",
              "Platform Account Name",
              "Platform Account Code",
              "Level",
          ] as const);

    const rows = mappings.map((m) => {
        const base: Record<string, string> = {
            "Third Account Code": m.thirdAccountCode,
            "Third Account Name": m.thirdAccountName,
            "Platform Account Name": m.yardiAccountName || "",
            "Platform Account Code": m.yardiAccountCode || "",
            Level: m.level,
        };
        if (!includeSecondComponent) return base;
        return {
            ...base,
            "Third Account Code - Secondary": m.thirdAccountCode2 ?? "",
            Type: mappingRowType(m),
            "Exclude from 2nd mapping": mappingExcludeLabel(m),
        };
    });

    return [headers, rows];
}

// The second key component, or a named fallback. Without the label the rows of a
// multi-criteria account differ only by an empty cell, which reads as missing data
// rather than as "this is the row a line falls back to".
const SecondComponentCell = ({ mapping }: { mapping: TCoAMappingView }) =>
    mapping.thirdAccountCode2 ? (
        <td className="px-4 py-3 font-mono text-xs">{mapping.thirdAccountCode2}</td>
    ) : (
        <td className="px-4 py-3 text-xs text-muted-foreground">
            {mapping.isFallback ? (
                <span title="Used when a line supplies no second component">Fallback</span>
            ) : (
                "—"
            )}
        </td>
    );

// Exclude / Type, rendered as the last two columns before Actions. Both are
// derived, so they are read-only here — the way to change them is a new CoA
// upload, which is also where they came from.
const ExcludeAndTypeCells = ({ mapping }: { mapping: TCoAMappingView }) => (
    <>
        <td className="px-4 py-3">{mappingExcludeLabel(mapping)}</td>
        <td className="px-4 py-3">{mappingRowType(mapping)}</td>
    </>
);

// Group header row for accounts with multiple mappings
const MappingGroupRow = memo(function MappingGroupRow({
    group,
    numberOptions,
    nameOptions,
    onUpdate,
    onDelete,
    canEditCoaLines,
    showSecondComponent,
}: {
    group: TMappingGroup;
    numberOptions: TSearchableSelectOption[];
    nameOptions: TSearchableSelectOption[];
    onUpdate: TUpdateFn;
    onDelete: TDeleteFn;
    canEditCoaLines: boolean;
    showSecondComponent: boolean;
}) {
    const [expanded, setExpanded] = useState(false);

    // Single mapping: render as a flat row
    if (group.mappings.length === 1) {
        return (
            <MappingRow
                mapping={group.mappings[0]!}
                numberOptions={numberOptions}
                nameOptions={nameOptions}
                onUpdate={onUpdate}
                onDelete={onDelete}
                canEditCoaLines={canEditCoaLines}
                showSecondComponent={showSecondComponent}
            />
        );
    }

    // Multiple mappings: expandable group
    return (
        <>
            <tr
                className="hover:bg-muted/50 cursor-pointer"
                onClick={() => setExpanded((prev) => !prev)}
            >
                <td className="px-4 py-3 font-mono text-xs">
                    <span className="flex items-center gap-1">
                        <ChevronRight
                            className={cn(
                                "h-4 w-4 transition-transform shrink-0",
                                expanded && "rotate-90",
                            )}
                        />
                        {group.thirdAccountCode}
                    </span>
                </td>
                {showSecondComponent && <td className="px-4 py-3" />}
                <td className="px-4 py-3">{group.thirdAccountName}</td>
            </tr>
            {expanded &&
                group.mappings.map((m) => (
                    <MappingDetailRow
                        key={m.id}
                        mapping={m}
                        numberOptions={numberOptions}
                        nameOptions={nameOptions}
                        onUpdate={onUpdate}
                        onDelete={onDelete}
                        canEditCoaLines={canEditCoaLines}
                        showSecondComponent={showSecondComponent}
                    />
                ))}
        </>
    );
});

// Detail row inside an expanded group (no Source Acct# / Name — shown in group header)
const MappingDetailRow = memo(function MappingDetailRow({
    mapping,
    numberOptions,
    nameOptions,
    onUpdate,
    onDelete,
    canEditCoaLines,
    showSecondComponent,
}: {
    mapping: TCoAMappingView;
    numberOptions: TSearchableSelectOption[];
    nameOptions: TSearchableSelectOption[];
    onUpdate: TUpdateFn;
    onDelete: TDeleteFn;
    canEditCoaLines: boolean;
    showSecondComponent: boolean;
}) {
    const [mode, setMode] = useState<"view" | "edit" | "delete">("view");
    const [draft, setDraft] = useState({
        level: mapping.level,
        isActive: mapping.isActive,
        yardiAccountId: mapping.yardiAccountId,
    });
    const [saving, setSaving] = useState(false);
    const [deleteError, setDeleteError] = useState<string | null>(null);

    const numberOpts = useMemo(
        () => ensureCurrentOption(numberOptions, mapping, mapping.yardiAccountCode),
        [numberOptions, mapping],
    );
    const nameOpts = useMemo(
        () => ensureCurrentOption(nameOptions, mapping, mapping.yardiAccountName),
        [nameOptions, mapping],
    );

    return (
        <tr
            className={cn(
                "hover:bg-muted/50 bg-muted/20",
                "even:bg-muted/40",
                !mapping.isInForce && "text-muted-foreground",
            )}
        >
            <td className="px-4 py-3 pl-10 font-mono text-xs text-muted-foreground">
                <span className="flex items-center gap-2">
                    <input
                        type="checkbox"
                        checked={mapping.isInForce}
                        disabled={saving || mapping.isInForce}
                        title={mapping.isInForce ? "This mapping is in force" : "Set as in force"}
                        className="h-4 w-4 cursor-pointer disabled:cursor-default"
                        onChange={async () => {
                            if (mapping.isInForce) return;
                            setSaving(true);
                            try {
                                await onUpdate(mapping.id, { is_in_force: true });
                            } catch (err) {
                                console.error(err);
                            } finally {
                                setSaving(false);
                            }
                        }}
                    />
                    <span className="text-[10px]">{mapping.isInForce ? "In Force" : ""}</span>
                </span>
            </td>
            {showSecondComponent && <SecondComponentCell mapping={mapping} />}
            <td className="px-4 py-3" />
            <td className="px-4 py-3">
                {mode === "edit" ? (
                    <select
                        value={draft.level}
                        onChange={(e) =>
                            setDraft((p) => ({ ...p, level: e.target.value as TCoALevel }))
                        }
                        className="w-14 border rounded px-1 py-1 text-xs"
                    >
                        {(["A", "P", "C"] as TCoALevel[]).map((l) => (
                            <option key={l} value={l}>
                                {l}
                            </option>
                        ))}
                    </select>
                ) : (
                    <span
                        className={cn(
                            "inline-flex items-center justify-center w-6 h-6 rounded text-xs font-bold",
                            mapping.level === "A"
                                ? "bg-blue-100 text-blue-700"
                                : mapping.level === "P"
                                  ? "bg-purple-100 text-purple-700"
                                  : "bg-amber-100 text-amber-700",
                        )}
                        title={levelLabels[mapping.level]}
                    >
                        {mapping.level}
                    </span>
                )}
            </td>
            <td className="px-4 py-3 font-mono text-xs">
                {mode === "edit" ? (
                    <LocalSearchableSelect
                        options={numberOpts}
                        value={draft.yardiAccountId}
                        onChange={(id) => setDraft((p) => ({ ...p, yardiAccountId: id }))}
                        className="font-mono"
                    />
                ) : (
                    mapping.yardiAccountCode || "—"
                )}
            </td>
            <td className="px-4 py-3">
                {mode === "edit" ? (
                    <LocalSearchableSelect
                        options={nameOpts}
                        value={draft.yardiAccountId}
                        onChange={(id) => setDraft((p) => ({ ...p, yardiAccountId: id }))}
                    />
                ) : (
                    mapping.yardiAccountName || "—"
                )}
            </td>
            {showSecondComponent && <ExcludeAndTypeCells mapping={mapping} />}
            <td className="px-4 py-3 text-right">
                {mode === "edit" ? (
                    <span className="flex gap-1 justify-end">
                        <Button
                            size="sm"
                            variant="default"
                            disabled={saving}
                            onClick={async () => {
                                setSaving(true);
                                try {
                                    await onUpdate(mapping.id, {
                                        level: draft.level,
                                        is_active: draft.isActive,
                                        yardi_account_id: draft.yardiAccountId,
                                    });
                                    setMode("view");
                                } catch (err) {
                                    console.error(err);
                                } finally {
                                    setSaving(false);
                                }
                            }}
                        >
                            {saving ? "Saving..." : "Save"}
                        </Button>
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                                setDraft({
                                    level: mapping.level,
                                    isActive: mapping.isActive,
                                    yardiAccountId: mapping.yardiAccountId,
                                });
                                setMode("view");
                            }}
                        >
                            Cancel
                        </Button>
                    </span>
                ) : mode === "delete" ? (
                    <span className="flex flex-col items-end gap-1">
                        <span className="flex gap-1">
                            <span className="text-xs text-muted-foreground">Delete?</span>
                            <Button
                                size="sm"
                                variant="destructive"
                                disabled={saving}
                                onClick={async () => {
                                    setSaving(true);
                                    setDeleteError(null);
                                    try {
                                        await onDelete(mapping.id);
                                    } catch (err: unknown) {
                                        const msg =
                                            err instanceof Error ? err.message : "Delete failed";
                                        setDeleteError(msg);
                                    } finally {
                                        setSaving(false);
                                    }
                                }}
                            >
                                Yes
                            </Button>
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                    setMode("view");
                                    setDeleteError(null);
                                }}
                            >
                                No
                            </Button>
                        </span>
                        {deleteError && (
                            <span className="text-xs text-destructive max-w-[200px] text-right">
                                {deleteError}
                            </span>
                        )}
                    </span>
                ) : (
                    <span className="flex gap-1 justify-end">
                        {canEditCoaLines && (
                            <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => {
                                    setDraft({
                                        level: mapping.level,
                                        isActive: mapping.isActive,
                                        yardiAccountId: mapping.yardiAccountId,
                                    });
                                    setMode("edit");
                                }}
                            >
                                Edit
                            </Button>
                        )}
                        {canEditCoaLines && (
                            <Button
                                size="sm"
                                variant="ghost"
                                className="text-destructive hover:text-destructive"
                                onClick={() => setMode("delete")}
                                disabled={mapping.isInForce}
                                title={
                                    mapping.isInForce
                                        ? "Set another mapping as in-force first"
                                        : "Delete mapping"
                                }
                            >
                                Delete
                            </Button>
                        )}
                    </span>
                )}
            </td>
        </tr>
    );
});

// Single flat row (no siblings) — standard 6-column layout
const MappingRow = memo(function MappingRow({
    mapping,
    numberOptions,
    nameOptions,
    onUpdate,
    onDelete,
    canEditCoaLines,
    showSecondComponent,
}: {
    mapping: TCoAMappingView;
    numberOptions: TSearchableSelectOption[];
    nameOptions: TSearchableSelectOption[];
    onUpdate: TUpdateFn;
    onDelete: TDeleteFn;
    canEditCoaLines: boolean;
    showSecondComponent: boolean;
}) {
    const [mode, setMode] = useState<"view" | "edit" | "delete">("view");
    const [draft, setDraft] = useState({
        level: mapping.level,
        isActive: mapping.isActive,
        yardiAccountId: mapping.yardiAccountId,
    });
    const [saving, setSaving] = useState(false);

    const numberOpts = useMemo(
        () => ensureCurrentOption(numberOptions, mapping, mapping.yardiAccountCode),
        [numberOptions, mapping],
    );
    const nameOpts = useMemo(
        () => ensureCurrentOption(nameOptions, mapping, mapping.yardiAccountName),
        [nameOptions, mapping],
    );

    return (
        <tr
            className={cn(
                "hover:bg-muted/50",
                "even:bg-muted/40",
                !mapping.isInForce && "text-muted-foreground",
            )}
        >
            <td className="px-4 py-3 font-mono text-xs">{mapping.thirdAccountCode}</td>
            {showSecondComponent && <SecondComponentCell mapping={mapping} />}
            <td className="px-4 py-3">{mapping.thirdAccountName}</td>
            <td className="px-4 py-3">
                {mode === "edit" ? (
                    <select
                        value={draft.level}
                        onChange={(e) =>
                            setDraft((p) => ({ ...p, level: e.target.value as TCoALevel }))
                        }
                        className="w-14 border rounded px-1 py-1 text-xs"
                    >
                        {(["A", "P", "C"] as TCoALevel[]).map((l) => (
                            <option key={l} value={l}>
                                {l}
                            </option>
                        ))}
                    </select>
                ) : (
                    <span
                        className={cn(
                            "inline-flex items-center justify-center w-6 h-6 rounded text-xs font-bold",
                            mapping.level === "A"
                                ? "bg-blue-100 text-blue-700"
                                : mapping.level === "P"
                                  ? "bg-purple-100 text-purple-700"
                                  : "bg-amber-100 text-amber-700",
                        )}
                        title={levelLabels[mapping.level]}
                    >
                        {mapping.level}
                    </span>
                )}
            </td>
            <td className="px-4 py-3 font-mono text-xs">
                {mode === "edit" ? (
                    <LocalSearchableSelect
                        options={numberOpts}
                        value={draft.yardiAccountId}
                        onChange={(id) => setDraft((p) => ({ ...p, yardiAccountId: id }))}
                        className="font-mono"
                    />
                ) : (
                    mapping.yardiAccountCode || "—"
                )}
            </td>
            <td className="px-4 py-3">
                {mode === "edit" ? (
                    <LocalSearchableSelect
                        options={nameOpts}
                        value={draft.yardiAccountId}
                        onChange={(id) => setDraft((p) => ({ ...p, yardiAccountId: id }))}
                    />
                ) : (
                    mapping.yardiAccountName || "—"
                )}
            </td>
            {showSecondComponent && <ExcludeAndTypeCells mapping={mapping} />}
            <td className="px-4 py-3 text-right">
                {mode === "edit" ? (
                    <span className="flex gap-1 justify-end">
                        <Button
                            size="sm"
                            variant="default"
                            disabled={saving}
                            onClick={async () => {
                                setSaving(true);
                                try {
                                    await onUpdate(mapping.id, {
                                        level: draft.level,
                                        is_active: draft.isActive,
                                        yardi_account_id: draft.yardiAccountId,
                                    });
                                    setMode("view");
                                } catch (err) {
                                    console.error(err);
                                } finally {
                                    setSaving(false);
                                }
                            }}
                        >
                            {saving ? "Saving..." : "Save"}
                        </Button>
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                                setDraft({
                                    level: mapping.level,
                                    isActive: mapping.isActive,
                                    yardiAccountId: mapping.yardiAccountId,
                                });
                                setMode("view");
                            }}
                        >
                            Cancel
                        </Button>
                    </span>
                ) : mode === "delete" ? (
                    <span className="flex gap-1 justify-end">
                        <span className="text-xs text-muted-foreground">Delete?</span>
                        <Button
                            size="sm"
                            variant="destructive"
                            disabled={saving}
                            onClick={async () => {
                                setSaving(true);
                                try {
                                    await onDelete(mapping.id);
                                } catch (err) {
                                    console.error(err);
                                } finally {
                                    setSaving(false);
                                    setMode("view");
                                }
                            }}
                        >
                            Yes
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setMode("view")}>
                            No
                        </Button>
                    </span>
                ) : (
                    <span className="flex gap-1 justify-end">
                        {canEditCoaLines && (
                            <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => {
                                    setDraft({
                                        level: mapping.level,
                                        isActive: mapping.isActive,
                                        yardiAccountId: mapping.yardiAccountId,
                                    });
                                    setMode("edit");
                                }}
                            >
                                Edit
                            </Button>
                        )}
                        {canEditCoaLines && (
                            <Button
                                size="sm"
                                variant="ghost"
                                className="text-destructive hover:text-destructive"
                                onClick={() => setMode("delete")}
                            >
                                Delete
                            </Button>
                        )}
                    </span>
                )}
            </td>
        </tr>
    );
});

type TCreateMappingRowProps = {
    numberOptions: TSearchableSelectOption[];
    nameOptions: TSearchableSelectOption[];
    onSave: (data: {
        third_account_code: string;
        third_account_name: string;
        yardi_account_id: string;
        level: TCoALevel;
    }) => Promise<void>;
    onCancel: () => void;
    showSecondComponent: boolean;
};

const CreateMappingRow = memo(function CreateMappingRow({
    numberOptions,
    nameOptions,
    onSave,
    onCancel,
    showSecondComponent,
}: TCreateMappingRowProps) {
    const [draft, setDraft] = useState({
        thirdAccountCode: "",
        thirdAccountName: "",
        yardiAccountId: "",
        level: "A" as TCoALevel,
    });
    const [saving, setSaving] = useState(false);

    const canSave =
        draft.thirdAccountCode.trim() !== "" &&
        draft.thirdAccountName.trim() !== "" &&
        draft.yardiAccountId !== "";

    return (
        <tr className="bg-blue-50/50">
            <td className="px-4 py-3">
                <input
                    type="text"
                    value={draft.thirdAccountCode}
                    onChange={(e) => setDraft((p) => ({ ...p, thirdAccountCode: e.target.value }))}
                    placeholder="Source Acct #"
                    className="w-full border rounded px-2 py-1 text-xs font-mono"
                />
            </td>
            {showSecondComponent && (
                <td
                    className="px-4 py-3 text-xs text-muted-foreground"
                    title="Second components come from the CoA upload, not this form"
                >
                    —
                </td>
            )}
            <td className="px-4 py-3">
                <input
                    type="text"
                    value={draft.thirdAccountName}
                    onChange={(e) => setDraft((p) => ({ ...p, thirdAccountName: e.target.value }))}
                    placeholder="Source Acct Name"
                    className="w-full border rounded px-2 py-1 text-xs"
                />
            </td>
            <td className="px-4 py-3">
                <select
                    value={draft.level}
                    onChange={(e) =>
                        setDraft((p) => ({ ...p, level: e.target.value as TCoALevel }))
                    }
                    className="w-14 border rounded px-1 py-1 text-xs"
                >
                    {(["A", "P", "C"] as TCoALevel[]).map((l) => (
                        <option key={l} value={l}>
                            {l}
                        </option>
                    ))}
                </select>
            </td>
            <td className="px-4 py-3">
                <LocalSearchableSelect
                    options={numberOptions}
                    value={draft.yardiAccountId}
                    onChange={(id) => setDraft((p) => ({ ...p, yardiAccountId: id }))}
                    className="font-mono"
                />
            </td>
            <td className="px-4 py-3">
                <LocalSearchableSelect
                    options={nameOptions}
                    value={draft.yardiAccountId}
                    onChange={(id) => setDraft((p) => ({ ...p, yardiAccountId: id }))}
                />
            </td>
            {showSecondComponent && (
                <>
                    <td className="px-4 py-3 text-muted-foreground">No</td>
                    <td className="px-4 py-3 text-muted-foreground">{ROW_TYPE_STANDARD}</td>
                </>
            )}
            <td className="px-4 py-3 text-right">
                <span className="flex gap-1 justify-end">
                    <Button
                        size="sm"
                        variant="default"
                        disabled={!canSave || saving}
                        onClick={async () => {
                            setSaving(true);
                            try {
                                await onSave({
                                    third_account_code: draft.thirdAccountCode.trim(),
                                    third_account_name: draft.thirdAccountName.trim(),
                                    yardi_account_id: draft.yardiAccountId,
                                    level: draft.level,
                                });
                            } catch (err) {
                                console.error(err);
                            } finally {
                                setSaving(false);
                            }
                        }}
                    >
                        {saving ? "Saving..." : "Save"}
                    </Button>
                    <Button size="sm" variant="outline" onClick={onCancel}>
                        Cancel
                    </Button>
                </span>
            </td>
        </tr>
    );
});
